import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import {
  isFutureYear,
  isValidDecimalWithNoTrailingZero,
  isValidIrreplaceabilityScore,
  isValidYearInput,
  isWholeNumber,
  toCriterionEScore
} from 'validators/criterionEValidators'
import { getCheckedReasonOptions, mapTriggerReasonToApi } from 'utils/criterionEHelpers'

export const setDisabled = (site, qualified, isEditable) =>
  site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qualified

export const setDisabledQualificationReason = (site, isEditable) =>
  site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)

export const setShowReassessmentFields = (criterionE, site, defaultPage) =>
  criterionE?.last_reassessment_date !== null && site.reassessmentFlag && !defaultPage

export const setShowFinishButton = (site, defaultPage) =>
  site.SiteStatusId === SITE_STATUS_DRAFT_ID && !defaultPage

export const hasTriggerReasons = (triggerReasonIds) =>
  Array.isArray(triggerReasonIds) && triggerReasonIds.length > 0

export const getInitialAssessmentYear = (year) => {
  if (!year) return null
  const dateObj = new Date(year)
  if (Number.isNaN(dateObj.getTime())) return null
  return dateObj.getFullYear().toString()
}

export const getInitialIrreplaceabilityScore = (scoreValue, isWholeNumber) => {
  if (scoreValue === undefined || scoreValue === null || scoreValue === '') return null
  const score = Number(scoreValue)
  if (Number.isNaN(score)) return null
  return isWholeNumber(score) ? score.toString() : score.toFixed(2)
}

export const resolveCriterionECheckFields = (siteProposalFieldValidation, criterionEId, defaultCriterionEValidation) =>
  (siteProposalFieldValidation?.criterionE && criterionEId && siteProposalFieldValidation.criterionE[criterionEId])
    ? siteProposalFieldValidation.criterionE[criterionEId]
    : defaultCriterionEValidation

export const isIrreplaceabilityInputDisabled = (site, isChecked) =>
  site.SiteStatusId !== SITE_STATUS_DRAFT_ID || !isChecked

export const createCriterionEHandlers = ({
  checkedValue,
  setCheckedOptions,
  setQualificationReason,
  setIsChecked,
  setReassessmentRational,
  setIrreplaceabilityScore,
  setAssessmentDate,
  persistField,
  updateTriggerReasonIdTimeOut,
  updateTriggerReasonTextTimeOut,
  updateIsCheckedTimeOut,
  updateIrreplaceabilityScoreTimeout,
  updateDateOfAssesmentTimeOut,
  yearInputLength
}) => ({
  handleChangeValueChecked: async (Ids) => {
    checkedValue.current = Ids
    setCheckedOptions(getCheckedReasonOptions(Ids))
  },
  handleChangeQualificationReason: async (e) => {
    try {
      if (!Array.isArray(e)) return
      setQualificationReason(e)
      const formattedReasons = mapTriggerReasonToApi(e)
      const triggerReasonParams = formattedReasons.includes(1)
        ? { trigger_reason_id: formattedReasons }
        : { trigger_reason_id: formattedReasons, irreplaceability_assessment_approved: false }
      updateTriggerReasonIdTimeOut(async () => {
        try {
          await persistField({ ...triggerReasonParams })
        } catch (error) {
          console.error('Error updating trigger reason:', error)
        }
      })
      if (formattedReasons.some(num => num > 1)) {
        setIsChecked(false)
      }
    } catch (error) {
      console.error('Error handling qualification reason change:', error)
    }
  },
  handleChangeReassessmentRational: async (e) => {
    try {
      setReassessmentRational(e.target.value)
      updateTriggerReasonTextTimeOut(async () => {
        try {
          await persistField({ trigger_reason_text: e.target.value })
        } catch (error) {
          console.error('Error updating reassessment rational:', error)
        }
      })
    } catch (error) {
      console.error('Error handling reassessment rational change:', error)
    }
  },
  handleCheckboxIrreplaceabilityChange: async (isChecked) => {
    try {
      const nextCheckedState = !isChecked
      setIsChecked(nextCheckedState)
      updateIsCheckedTimeOut(async () => {
        try {
          await persistField({ irreplaceability_assessment_approved: nextCheckedState })
        } catch (error) {
          console.error('Error updating checkbox:', error)
        }
      })
    } catch (error) {
      console.error('Error handling checkbox change:', error)
    }
  },
  handleChangeIrreplaceabilityScore: async (e) => {
    try {
      const value = e.target.value
      if (!isValidIrreplaceabilityScore(value)) return

      if (isWholeNumber(value)) {
        setIrreplaceabilityScore(value)
        updateIrreplaceabilityScoreTimeout(async () => {
          try {
            await persistField({ irreplaceability_score: Number(value) || null })
          } catch (error) {
            console.error('Error updating irreplaceability score:', error)
          }
        })
        return
      }

      if (isValidDecimalWithNoTrailingZero(value)) {
        setIrreplaceabilityScore(value)
        const formattedValue = toCriterionEScore(value)
        updateIrreplaceabilityScoreTimeout(async () => {
          try {
            await persistField({ irreplaceability_score: formattedValue || null })
          } catch (error) {
            console.error('Error updating irreplaceability score:', error)
          }
        })
      }
    } catch (error) {
      console.error('Error handling irreplaceability score change:', error)
    }
  },
  handleChangeAssessmentDate: async (e) => {
    try {
      const yearInput = e.target.value
      if (!isValidYearInput(yearInput, yearInputLength)) return

      setAssessmentDate(yearInput)
      if (yearInput.length !== yearInputLength && yearInput !== '') return
      if (isFutureYear(yearInput)) return

      const fullDate = yearInput ? `${yearInput}-01-01` : null
      updateDateOfAssesmentTimeOut(async () => {
        try {
          await persistField({ year: fullDate })
        } catch (error) {
          console.error('Error updating assessment date:', error)
        }
      })
    } catch (error) {
      console.error('Error handling assessment date change:', error)
    }
  }
})
